import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import api from "../../api";
import { LuUser } from "react-icons/lu";
import { BsPencil, BsFillTrash3Fill } from "react-icons/bs";
import Tooltip from "@mui/material/Tooltip";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

export default function CollagesList() {
  const [collages, setCollages] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);

  const fetchColleges = async () => {
    const { data } = await api.get(
      `${process.env.REACT_APP_API_BASE_URL}colleges/approved`
    );
    setCollages(data);
  };

  useEffect(() => {
    fetchColleges();
  }, []);

  const handleEditOpen = (college) => {
    setSelectedCollege({ ...college });
    setOpenEditModal(true);
  };

  const handleProfileOpen = (college) => {
    setSelectedCollege(college);
    setOpenProfileModal(true);
  };

  const handleModalClose = () => {
    setSelectedCollege(null);
    setOpenEditModal(false);
    setOpenProfileModal(false);
  };

  const handleEditSave = async () => {
    try {
      await api.put(
        `${process.env.REACT_APP_API_BASE_URL}colleges/edit/${selectedCollege._id}`,
        selectedCollege
      );
      await fetchColleges();
      handleModalClose();
    } catch (err) {
      alert("Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this college?")) {
      try {
        await api.delete(`${process.env.REACT_APP_API_BASE_URL}colleges/delete/${id}`);
        setCollages(collages.filter((c) => c._id !== id));
      } catch (error) {
        alert("Delete failed");
      }
    }
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 2, minWidth: 300, headerClassName: "custom-header" },
    { field: "email", headerName: "Email", flex: 3, minWidth: 250, headerClassName: "custom-header" },
    { field: "address", headerName: "Address", flex: 3, minWidth: 100, headerClassName: "custom-header" },
    {
      field: "action",
      headerName: "Action",
      flex: 3,
      minWidth: 100,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <div className="flex justify-between py-4 pe-6 gap-3">
          <Tooltip title="Profile">
            <LuUser className="cursor-pointer hover:text-blue-400 transition-colors duration-200" onClick={() => handleProfileOpen(params.row)} />
          </Tooltip>
          <Tooltip title="Edit">
            <BsPencil className="cursor-pointer hover:text-blue-400 transition-colors duration-200" onClick={() => handleEditOpen(params.row)} />
          </Tooltip>
          <Tooltip title="Delete">
            <BsFillTrash3Fill className="cursor-pointer hover:text-blue-400 transition-colors duration-200" onClick={() => handleDelete(params.row._id)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/10">
<DataGrid
  rows={collages || []}
  columns={columns}
  getRowId={(row) => row._id}
  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
  pageSizeOptions={[5, 10]}
  sx={{
    // Glassy root container
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: "none",
    borderRadius: 3,
    backdropFilter: "blur(8px)",
  
    // Top header row
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      backdropFilter: "blur(12px)",
      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
    },
  
    // Footer container (bottom row)
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: "rgba(255,255,255,0.05)",
      borderTop: "1px solid rgba(255,255,255,0.1)",
      backdropFilter: "blur(10px)",
    },
  
    // Ensure rows have no override from CSS variable
    "& .MuiDataGrid-row": {
      backgroundColor: "transparent !important",
    },
  
    // Optional: override containerBackground if needed
    "--DataGrid-containerBackground": "transparent",
  }}
/>

      {/* 🔧 Edit Modal */}
      <Modal open={openEditModal} onClose={handleModalClose}>
  <Box
    sx={{
      width: 400,
      margin: "10% auto",
      padding: 4,
      borderRadius: 4,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(20px)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
      color: "#fff",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <h2 style={{ fontSize: "1.25rem", fontWeight: "bold" }}>Edit College</h2>
    <TextField
      fullWidth
      label="Name"
      variant="outlined"
      value={selectedCollege?.name || ""}
      onChange={(e) =>
        setSelectedCollege({ ...selectedCollege, name: e.target.value })
      }
      InputProps={{ style: { color: "#fff" } }}
      InputLabelProps={{ style: { color: "#ccc" } }}
    />
    <TextField
      fullWidth
      label="Email"
      variant="outlined"
      value={selectedCollege?.email || ""}
      onChange={(e) =>
        setSelectedCollege({ ...selectedCollege, email: e.target.value })
      }
      InputProps={{ style: { color: "#fff" } }}
      InputLabelProps={{ style: { color: "#ccc" } }}
    />
    <TextField
      fullWidth
      label="Address"
      variant="outlined"
      value={selectedCollege?.address || ""}
      onChange={(e) =>
        setSelectedCollege({ ...selectedCollege, address: e.target.value })
      }
      InputProps={{ style: { color: "#fff" } }}
      InputLabelProps={{ style: { color: "#ccc" } }}
    />
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
      <Button onClick={handleModalClose} variant="outlined" color="inherit">
        Cancel
      </Button>
      <Button variant="contained" onClick={handleEditSave}>
        Save
      </Button>
    </div>
  </Box>
</Modal>

      {/* 👤 Profile Modal */}
      <Modal open={openProfileModal} onClose={handleModalClose}>
  <Box
    sx={{
      width: 400,
      margin: "10% auto",
      padding: 4,
      borderRadius: 4,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(20px)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
      color: "#fff",
    }}
  >
    <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", marginBottom: "1rem" }}>
      College Profile
    </h2>
    <p style={{ marginBottom: "0.5rem" }}>
      <strong>Name:</strong> {selectedCollege?.name}
    </p>
    <p style={{ marginBottom: "0.5rem" }}>
      <strong>Email:</strong> {selectedCollege?.email}
    </p>
    <p style={{ marginBottom: "1rem" }}>
      <strong>Address:</strong> {selectedCollege?.address}
    </p>
    <div style={{ textAlign: "right" }}>
      <Button onClick={handleModalClose} variant="outlined" color="inherit">
        Close
      </Button>
    </div>
  </Box>
</Modal>
    </div>
  );
}
